import { useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { Box, Button, FormDialog, Typography, BaseTable, TableBody, TableRow, TableCell } from "flux";
import { BaseDialogProps } from "@adora/shared/contexts/dialog-context";
import { RelativeUrl } from "@adora/shared/utils/environment";
import post from "@adora/shared/utils/post";
import { UsersUrl } from "../../hooks/use-users";

interface CsvRow {
  brand: string;
  account_type: string;
  username: string;
  password: string;
  email: string;
}

interface BulkCreateUserResult {
  username: string;
  success: boolean;
  error?: string;
  user_id?: number;
}

interface BulkCreateUsersResponse {
  results: BulkCreateUserResult[];
  total: number;
  succeeded: number;
  failed: number;
}

const REQUIRED_HEADERS = ["brand", "account_type", "username", "password", "email"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const HEADER_SX = { fontWeight: 600, fontSize: "13px", padding: "6px 12px" };
const CELL_SX = { fontSize: "13px", padding: "4px 12px" };
const HEADER_ROW_SX = { background: "rgba(0,0,0,0.04)" };
const SCROLL_BOX_SX = {
  maxHeight: "200px",
  overflowY: "auto",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: "4px"
};
const RESULTS_BOX_SX = {
  maxHeight: "300px",
  overflowY: "auto",
  border: "1px solid rgba(0,0,0,0.12)",
  borderRadius: "4px"
};

function parseCsv(text: string): CsvRow[] | string {
  const lines = text.trim().replaceAll("\r\n", "\n").replaceAll("\r", "\n").split("\n");
  if (lines.length < 2) return "CSV must have a header row and at least one data row.";

  const headers = lines[0]
    .trim()
    .toLowerCase()
    .split(",")
    .map((h) => h.trim());

  for (const required of REQUIRED_HEADERS) {
    if (!headers.includes(required)) {
      return `Missing required column: "${required}". Required columns: ${REQUIRED_HEADERS.join(", ")}`;
    }
  }

  const rows: CsvRow[] = [];
  const invalidEmails: string[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = line.split(",").map((v) => v.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] ?? "";
    });
    const email = (row.email ?? "").trim();
    if (email && !EMAIL_RE.test(email)) {
      invalidEmails.push(`Row ${i}: "${email}"`);
    }

    rows.push({
      brand: row.brand,
      account_type: row.account_type,
      username: row.username,
      password: row.password,
      email
    });
  }

  if (invalidEmails.length > 0) {
    return `Invalid email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`;
  }

  return rows;
}

const BulkCreateUsersDialog: React.FC<BaseDialogProps> = ({ open, onClose }) => {
  const [parseError, setParseError] = useState<string>("");
  const [submitError, setSubmitError] = useState<string>("");
  const [rows, setRows] = useState<CsvRow[] | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<BulkCreateUsersResponse | null>(null);
  // use-users.tsx fetches via SWR (legacy), so cache invalidation must go through
  // SWR's mutate. When use-users.tsx is migrated to TanStack Query, replace this
  // with useQueryClient().invalidateQueries({ queryKey: queryKeys.users() }).
  const { mutate } = useSWRConfig();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParseError("");
    setSubmitError("");
    setResults(null);
    setRows(null);

    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    file
      .text()
      .then((text) => {
        const parsed = parseCsv(text);
        if (typeof parsed === "string") {
          setParseError(parsed);
        } else {
          setRows(parsed);
        }
      })
      .catch(() => setParseError("Failed to read file. Please try again."));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (results) {
      handleClose();
      return;
    }
    if (!rows || rows.length === 0) return;

    setSubmitting(true);
    setSubmitError("");
    const body = { users: rows.map((r): Record<string, string> => ({ ...r })) };
    try {
      const result = await post<BulkCreateUsersResponse>(`${RelativeUrl}/${UsersUrl}/bulk`, {}, body);
      result.match(
        (err) => setSubmitError(err.message),
        (data) => {
          setResults(data);
          if (data.succeeded > 0) {
            const cacheKeyPattern = new RegExp(`^${RelativeUrl}/${UsersUrl}`);
            mutate((key) => typeof key === "string" && cacheKeyPattern.test(key), undefined, { revalidate: true });
          }
        }
      );
    } catch {
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose(results !== null && results.succeeded > 0);
  };

  return (
    <FormDialog
      title="Bulk Create Users"
      description="Upload a CSV with columns: brand, account_type, username, password, email"
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      primaryButtonText={results ? "Done" : "Create Users"}
      isPrimaryButtonDisabled={submitting || (!results && (!rows || rows.length === 0))}
      dataDogIdentifier="bulk create users"
    >
      <Box display="flex" flexDirection="column" gap="16px">
        {!results && (
          <>
            <Box>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => fileInputRef.current?.click()}
                dataDogIdentifier="bulk create users upload csv"
              >
                {fileName ? `${fileName}` : "Choose CSV File"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Box>

            {parseError && (
              <Typography color="error" variant="body2">
                {parseError}
              </Typography>
            )}

            {submitError && (
              <Typography color="error" variant="body2">
                {submitError}
              </Typography>
            )}

            {rows && rows.length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" mb="8px">
                  {rows.length} user{rows.length !== 1 ? "s" : ""} ready to create:
                </Typography>
                <Box sx={SCROLL_BOX_SX}>
                  <BaseTable size="small">
                    <TableBody>
                      <TableRow sx={HEADER_ROW_SX}>
                        {REQUIRED_HEADERS.map((h) => (
                          <TableCell key={h} sx={HEADER_SX}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                      {rows.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell sx={CELL_SX}>{row.brand}</TableCell>
                          <TableCell sx={CELL_SX}>{row.account_type}</TableCell>
                          <TableCell sx={CELL_SX}>{row.username}</TableCell>
                          <TableCell sx={CELL_SX}>{"•".repeat(Math.min(row.password.length, 8))}</TableCell>
                          <TableCell sx={CELL_SX}>{row.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </BaseTable>
                </Box>
              </Box>
            )}
          </>
        )}

        {results && (
          <Box>
            <Box display="flex" gap="24px" mb="12px">
              <Typography variant="body1">
                <strong>{results.succeeded}</strong> succeeded
              </Typography>
              <Typography variant="body1" color={results.failed > 0 ? "error" : "text.primary"}>
                <strong>{results.failed}</strong> failed
              </Typography>
            </Box>
            <Box sx={RESULTS_BOX_SX}>
              <BaseTable size="small">
                <TableBody>
                  <TableRow sx={HEADER_ROW_SX}>
                    <TableCell sx={HEADER_SX}>Username</TableCell>
                    <TableCell sx={HEADER_SX}>Status</TableCell>
                    <TableCell sx={HEADER_SX}>Details</TableCell>
                  </TableRow>
                  {results.results.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={CELL_SX}>{r.username}</TableCell>
                      <TableCell sx={{ ...CELL_SX, color: r.success ? "green" : "red" }}>
                        <span aria-label={r.success ? "Created" : "Failed"}>
                          {r.success ? "✓ Created" : "✗ Failed"}
                        </span>
                      </TableCell>
                      <TableCell sx={CELL_SX}>{r.success ? `ID: ${r.user_id}` : r.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </BaseTable>
            </Box>
          </Box>
        )}
      </Box>
    </FormDialog>
  );
};

export default BulkCreateUsersDialog;
